import { Button, EmptyState, ToneIcon } from "@ternary/ui"
import type { AriaRole } from "react"

/**
 * A page or list that could not load: the danger empty state, with what failed as its title, plain
 * advice, Try again, and the API's own words only under Details. In an alert, so it is read out as
 * it appears; inside a Table's error slot, which is an alert already, pass `role="none"`.
 */
export function LoadError({
  message,
  description = "Check your connection and try again.",
  detail,
  onRetry,
  retrying = false,
  role = "alert",
}: {
  /** What failed, such as "We couldn't load your products": a heading, so no full stop. */
  message: string
  /** What to do. Default: "Check your connection and try again." */
  description?: string | undefined
  /** The API's message, shown under Details. */
  detail?: string | undefined
  onRetry(): void
  retrying?: boolean | undefined
  /** Default: "alert". "none" inside a region that announces it already, so no alert nests. */
  role?: AriaRole | undefined
}) {
  return (
    <div className="admin-load-error" role={role}>
      <EmptyState
        action={
          <>
            <Button loading={retrying} onClick={onRetry} variant="secondary">
              Try again
            </Button>
            {detail ? (
              <details className="admin-load-error-details">
                <summary>Details</summary>
                <p>{detail}</p>
              </details>
            ) : null}
          </>
        }
        description={description}
        icon={<ToneIcon tone="danger" />}
        title={message}
        tone="danger"
      />
    </div>
  )
}

/** The API's message for a failed query, when it sent one. */
export function errorDetail(error: unknown): string | undefined {
  return error instanceof Error && error.message ? error.message : undefined
}
